"use client";

/**
 * CarouselComponent
 * Generic image carousel for landing pages with navigation and auto-play
 */

import { LandingPageComponent, CarouselConfig } from "@/lib/database/landing-pages";
import { generateColorPalette, DEFAULT_PRIMARY_COLOR } from "../../../_logic/color-utils";
import { useState, useEffect, useCallback, useMemo } from "react";

interface CarouselComponentProps {
  component: LandingPageComponent;
  primaryColor?: string;
}

// Constants
const AUTO_PLAY_ENABLED = true;
const AUTO_PLAY_INTERVAL = 5000; // 5 seconds between slide transitions
const CAROUSEL_HEIGHT = "500px"; // Fixed height for consistent image display

export default function CarouselComponent({ component, primaryColor }: CarouselComponentProps) {
  // Config
  const config = (component.config || {}) as CarouselConfig;

  // Color palette
  const palette = useMemo(() => generateColorPalette(primaryColor || DEFAULT_PRIMARY_COLOR), [primaryColor]);

  // Constants
  const title = config.title;
  const images = config.images || [];

  // States
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Handlers
  const goToSlide = useCallback((index: number) => {
    setCurrentIndex(index);
  }, []);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
  }, [images.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
  }, [images.length]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        goToNext();
      }
    },
    [goToPrevious, goToNext]
  );

  const openModal = useCallback((index: number) => {
    setModalImageIndex(index);
    setIsModalOpen(true);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 0.5, 0.5));
  }, []);

  const goToModalPrevious = useCallback(() => {
    setModalImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [images.length]);

  const goToModalNext = useCallback(() => {
    setModalImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [images.length]);

  // Pan/drag handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoomLevel > 1) {
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      }
    },
    [zoomLevel, position]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && zoomLevel > 1) {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        // Calculate bounds to prevent dragging to empty space
        const container = e.currentTarget as HTMLElement;
        const maxX = (container.offsetWidth * (zoomLevel - 1)) / 2;
        const maxY = (container.offsetHeight * (zoomLevel - 1)) / 2;

        setPosition({
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        });
      }
    },
    [isDragging, dragStart, zoomLevel]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Effects
  useEffect(() => {
    if (!AUTO_PLAY_ENABLED || images.length <= 1) return;

    const interval = setInterval(() => {
      goToNext();
    }, AUTO_PLAY_INTERVAL);

    return () => clearInterval(interval);
  }, [images.length, goToNext]);

  // Render
  if (images.length === 0) {
    return (
      <div className="bg-white py-20">
        <div className="container mx-auto px-4">
          <p className="text-gray-500 text-center">No images configured yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white py-20">
      <div className="container mx-auto px-4">
        {title && <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">{title}</h2>}
        <div className="max-w-4xl mx-auto relative">
          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrevious();
                }}
                className="absolute left-4 md:left-2 top-1/2 -translate-y-1/2 z-20 bg-white/80 hover:bg-white text-gray-900 p-2 md:p-3 rounded-lg shadow-md transition-colors touch-manipulation"
                aria-label="Previous slide"
                tabIndex={0}
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={goToNext}
                className="absolute right-4 md:right-2 top-1/2 -translate-y-1/2 z-10 bg-white/80 hover:bg-white text-gray-900 p-2 md:p-3 rounded-lg shadow-md transition-colors touch-manipulation"
                aria-label="Next slide"
                tabIndex={0}
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image Container */}
          <div
            className="relative rounded-lg overflow-hidden shadow-sm"
            onKeyDown={handleKeyDown}
            tabIndex={0}
            role="region"
            aria-label="Image carousel"
            style={{ height: CAROUSEL_HEIGHT }}
          >
            {images.map((image, index) => (
              <button
                key={`${image}-${index}`}
                type="button"
                onClick={() => openModal(index)}
                className={`absolute inset-0 w-full h-full transition-opacity duration-500 touch-manipulation ${
                  index === currentIndex ? "opacity-100 cursor-pointer" : "opacity-0 pointer-events-none"
                }`}
                aria-label={`View slide ${index + 1}`}
              >
                <img
                  src={image}
                  alt={`Slide ${index + 1}`}
                  className="w-full h-full object-contain hover:opacity-90 transition-opacity"
                />
              </button>
            ))}
          </div>

          {/* Dots Indicator */}
          {images.length > 1 && (
            <div className="text-center mt-6">
              <div className="flex justify-center space-x-2">
                {images.map((_, index) => (
                  <button
                    type="button"
                    key={index}
                    onClick={() => goToSlide(index)}
                    className={`w-2 h-2 rounded-full transition-colors touch-manipulation ${index === currentIndex ? "" : "bg-gray-300"}`}
                    style={index === currentIndex ? { backgroundColor: palette.primary } : {}}
                    aria-label={`Go to slide ${index + 1}`}
                    tabIndex={0}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Image Modal with Zoom */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop button for closing */}
          <button
            type="button"
            className="absolute inset-0 bg-black bg-opacity-90 touch-manipulation"
            onClick={closeModal}
            aria-label="Close modal"
            tabIndex={-1}
          />
          {/* Close Button */}
          <button
            type="button"
            onClick={closeModal}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50 touch-manipulation"
            aria-label="Close modal"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Zoom Controls */}
          <div className="absolute top-4 left-4 flex flex-col gap-2 z-50">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                zoomIn();
              }}
              className="bg-white text-gray-900 hover:bg-gray-100 p-2 rounded-lg shadow-md touch-manipulation"
              aria-label="Zoom in"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                zoomOut();
              }}
              className="bg-white text-gray-900 hover:bg-gray-100 p-2 rounded-lg shadow-md touch-manipulation"
              aria-label="Zoom out"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                />
              </svg>
            </button>
            <div className="bg-white text-gray-900 px-3 py-1 rounded-lg shadow-md text-sm text-center">
              {Math.round(zoomLevel * 100)}%
            </div>
          </div>

          {/* Navigation Arrows */}
          {images.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToModalPrevious();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-900 p-3 rounded-lg shadow-md z-50 touch-manipulation"
                aria-label="Previous image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToModalNext();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-900 p-3 rounded-lg shadow-md z-50 touch-manipulation"
                aria-label="Next image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image */}
          <div
            className="overflow-hidden max-w-full max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
          >
            <img
              src={images[modalImageIndex]}
              alt={`Slide ${modalImageIndex + 1}`}
              className="max-w-none transition-transform duration-200 select-none"
              draggable={false}
              style={{
                transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                transformOrigin: "center center",
              }}
            />
          </div>

          {/* Image Counter */}
          {images.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-lg">
              {modalImageIndex + 1} / {images.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
